import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ProjectOption } from '@/components/layout/ProjectSelector';
import { applyAccent } from '@/lib/accent';

/** The empty id means every project — the "All projects" choice. */
export const ALL_PROJECTS = '';

const STORAGE_KEY = 'oneparacon.current-project';

interface CurrentProject {
  /** A project's public id, or ALL_PROJECTS. */
  projectId: string;
  /** The chosen project, or null when looking at all of them. */
  project: ProjectOption | null;
  projects: ProjectOption[];
  isAll: boolean;
  setProjectId: (id: string) => void;
}

const Context = createContext<CurrentProject | null>(null);

/**
 * The project the whole app is looking at, chosen in the sidebar.
 *
 * One selection, shared: pick a project and Home, Site Management, People,
 * Insights, Finance, the programme and dashboard widgets all show that job;
 * pick All projects and they show everything. Screens with their own project
 * filter bind it to this rather than keeping a second, disagreeing choice.
 *
 * Remembered on this device, because someone who runs one site opens the app
 * to that site every morning.
 */
export function CurrentProjectProvider({ projects, children }: { projects: ProjectOption[]; children: ReactNode }) {
  const [projectId, setStored] = useState<string>(() => {
    try {
      return window.localStorage.getItem(STORAGE_KEY) ?? ALL_PROJECTS;
    } catch {
      return ALL_PROJECTS;
    }
  });

  const setProjectId = useCallback((id: string) => {
    setStored(id);
    try {
      window.localStorage.setItem(STORAGE_KEY, id);
    } catch {
      // Private browsing — the choice holds for this session only.
    }
  }, []);

  // A remembered project that has since closed or been removed falls back to all.
  useEffect(() => {
    if (projectId && projects.length > 0 && !projects.some((project) => project.id === projectId)) {
      setProjectId(ALL_PROJECTS);
    }
  }, [projects, projectId, setProjectId]);

  const project = projects.find((entry) => entry.id === projectId) ?? null;

  // The whole app takes the selected project's colour; All projects is the brand accent.
  useEffect(() => {
    applyAccent(project?.color);
  }, [project?.color]);

  const value = useMemo<CurrentProject>(() => {
    const project = projects.find((entry) => entry.id === projectId) ?? null;
    return { projectId: project ? projectId : ALL_PROJECTS, project, projects, isAll: !project, setProjectId };
  }, [projects, projectId, setProjectId]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useCurrentProject(): CurrentProject {
  const value = useContext(Context);
  if (!value) {
    // Outside the shell (public pages) there is no selection: everything.
    return { projectId: ALL_PROJECTS, project: null, projects: [], isAll: true, setProjectId: () => undefined };
  }
  return value;
}
